import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { UploadSimple, Trash, FilePdf, FileImage, DownloadSimple, WarningCircle, Clock, MagicWand, Star, Sparkle, PencilSimple, ArrowRight } from "@phosphor-icons/react";

const TYPES = ["cv", "cover_letter", "certificate", "transcript", "id", "passport", "brp", "share_code", "birth_certificate", "settled_status_proof", "reference", "other"];

function ExpiryBadge({ days }) {
  if (days == null) return null;
  if (days < 0) {
    return (
      <span data-testid="expiry-badge" className="inline-flex items-center gap-1 text-xs font-mono-d px-2 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-peach)]">
        <WarningCircle size={12} weight="fill"/> expired {-days}d ago
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span data-testid="expiry-badge" className="inline-flex items-center gap-1 text-xs font-mono-d px-2 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-yellow)]">
        <Clock size={12} weight="fill"/> {days}d left
      </span>
    );
  }
  return null;
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [expiryMap, setExpiryMap] = useState({});
  const [expiry, setExpiry] = useState({ counts: { expired: 0, expiring_soon: 0, total: 0 } });
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("certificate");
  const [tags, setTags] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [renameTo, setRenameTo] = useState("");
  // Generated documents (Vault Coach output — saved_letters collection)
  const [generated, setGenerated] = useState([]);
  const [editingGen, setEditingGen] = useState(null);
  const [genRenameTo, setGenRenameTo] = useState("");
  const [tab, setTab] = useState("generated"); // "generated" | "uploaded"

  const load = async () => {
    const [docsRes, expRes, genRes] = await Promise.all([
      api.get("/documents"),
      api.get("/documents/expiry/status"),
      api.get("/saved-letters?kind=cv_master,cv_tailored,cover_letter_master,cover_letter"),
    ]);
    setDocs(docsRes.data);
    setExpiry(expRes.data);
    setGenerated(genRes.data);
    const map = {};
    [...expRes.data.expired, ...expRes.data.expiring_soon].forEach((d) => { map[d.id] = d.days_until_expiry; });
    setExpiryMap(map);
  };
  useEffect(() => {
    load();
    api.get("/profile").then(({ data }) => {
      setProfileName(data?.personal_info?.full_name || "");
    }).catch(() => {});
  }, []);

  // ---- employer-friendly naming helper ----
  // Format: Firstname-Lastname-DocType[-Date].ext  e.g. "Jane-Doe-CV-2026.pdf"
  const suggestFilename = () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    if (!profileName) {
      toast.error("Add your full name in Profile first — needed for the suggestion");
      return;
    }
    const nameParts = profileName.trim().split(/\s+/);
    const first = nameParts[0] || "";
    const last = nameParts.slice(1).join("-") || "";
    const typeMap = {
      cv: "CV",
      cover_letter: "Cover-Letter",
      certificate: "Certificate",
      transcript: "Transcript",
      id: "ID",
      passport: "Passport",
      brp: "BRP",
      share_code: "Share-Code",
      birth_certificate: "Birth-Certificate",
      settled_status_proof: "Settled-Status",
      reference: "Reference",
      other: "Document",
    };
    const typeLabel = typeMap[docType] || "Document";
    const year = new Date().getFullYear();
    const ext = file.name.split(".").pop() || "pdf";
    const baseName = [first, last, typeLabel, year].filter(Boolean).join("-").replace(/--+/g, "-");
    const suggestion = `${baseName}.${ext}`;
    setRenameTo(suggestion);
    toast.success(`Suggested: ${suggestion}`);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      // If user picked a renamed filename, build a new File from the blob
      const uploadFile = renameTo
        ? new File([file], renameTo, { type: file.type })
        : file;
      const fd = new FormData();
      fd.append("file", uploadFile);
      await api.post(`/documents?doc_type=${encodeURIComponent(docType)}&tags=${encodeURIComponent(tags)}&expiry=${encodeURIComponent(expiryDate)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document vaulted");
      setFile(null); setTags(""); setExpiryDate(""); setRenameTo("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (id, filename) => {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    if (!confirm("Remove document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Removed");
    load();
  };

  // Toggle "primary" for this doc-type. The extension reads `is_primary` when
  // deciding which CV / cover letter / passport / etc. to auto-attach when
  // multiple docs of the same type exist.
  const setPrimary = async (id) => {
    try {
      await api.post(`/documents/${id}/set-primary`);
      toast.success("Set as primary for autofill");
      load();
    } catch { toast.error("Couldn't set as primary"); }
  };

  return (
    <AppShell title="Document Safe">
      <p className="text-[var(--brand-muted)] mb-6 max-w-2xl">
        the permanent library. every CV / cover letter you generate in Vault Coach lands here automatically. plus certs, transcripts, IDs — uploaded once, tagged, dated.
      </p>

      {/* Tab switcher — generated (from Vault Coach) vs uploaded (files) */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="docs-tabs">
        <button onClick={() => setTab("generated")} data-testid="tab-generated" aria-pressed={tab === "generated"}
          className={`btn-hard rounded-md px-4 py-2 text-sm font-mono-d flex items-center gap-2 ${tab === "generated" ? "bg-[var(--brand-ink)] text-white" : "bg-white"}`}>
          <Sparkle size={14} weight="bold"/> generated ({generated.length})
        </button>
        <button onClick={() => setTab("uploaded")} data-testid="tab-uploaded" aria-pressed={tab === "uploaded"}
          className={`btn-hard rounded-md px-4 py-2 text-sm font-mono-d flex items-center gap-2 ${tab === "uploaded" ? "bg-[var(--brand-ink)] text-white" : "bg-white"}`}>
          <UploadSimple size={14} weight="bold"/> uploaded ({docs.length})
        </button>
      </div>

      {(expiry.counts.expired > 0 || expiry.counts.expiring_soon > 0) && tab === "uploaded" && (
        <div className="card-hard rounded-md p-5 bg-[var(--pastel-peach)] mb-6" data-testid="expiry-banner">
          <p className="label-tiny mb-2 flex items-center gap-2"><WarningCircle weight="fill" size={14}/> heads up</p>
          <p className="text-sm">
            {expiry.counts.expired > 0 && <><b>{expiry.counts.expired}</b> document{expiry.counts.expired > 1 ? "s" : ""} expired. </>}
            {expiry.counts.expiring_soon > 0 && <><b>{expiry.counts.expiring_soon}</b> expiring in the next 30 days.</>}
          </p>
        </div>
      )}

      {tab === "generated" && (
        <div data-testid="generated-panel">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)] mb-6">
            <p className="label-tiny mb-2">/generated in vault coach</p>
            <p className="text-sm text-[var(--brand-ink)]/80">CVs and cover letters generated using the information stored in your vault. ⭐ Master versions lead each type. 📄 Tailored versions are per-role.</p>
            <Link to="/vault-coach" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 mt-3">
              <Sparkle size={14} weight="bold"/> open vault coach <ArrowRight size={12} weight="bold"/>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="generated-grid">
            {generated.length === 0 && (
              <p className="text-sm text-[var(--brand-muted)]">no generated documents yet. head to <Link to="/vault-coach" className="underline">Vault Coach</Link> to create a Master CV or a tailored letter.</p>
            )}
            {generated.map((g) => {
              const isMaster = g.kind.endsWith("_master");
              const isCV = g.kind.startsWith("cv");
              return (
                <div key={g.id} className={`card-hard rounded-md p-5 ${isMaster ? "bg-[var(--pastel-yellow)]" : "bg-white"}`} data-testid={`gen-${g.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="label-tiny">{isMaster ? "⭐ MASTER" : "📄 TAILORED"}</span>
                      <span className="label-tiny">{isCV ? "CV" : "COVER"}</span>
                    </div>
                  </div>
                  {editingGen === g.id ? (
                    <div className="mt-2 flex gap-1 items-center">
                      <input value={genRenameTo} onChange={(e) => setGenRenameTo(e.target.value)}
                        data-testid={`gen-rename-input-${g.id}`}
                        className="card-hard rounded-md px-2 py-1 text-xs flex-1"/>
                      <button onClick={async () => {
                        const t = genRenameTo.trim();
                        if (t.length < 2) { toast.error("give it a real name"); return; }
                        try { await api.patch(`/saved-letters/${g.id}`, { title: t }); toast.success("renamed"); setEditingGen(null); setGenRenameTo(""); load(); }
                        catch { toast.error("rename failed"); }
                      }} data-testid={`gen-rename-save-${g.id}`} className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-2 py-1 text-xs">save</button>
                      <button onClick={() => { setEditingGen(null); setGenRenameTo(""); }} className="btn-hard bg-white rounded-md px-2 py-1 text-xs">✕</button>
                    </div>
                  ) : (
                    <p className="font-medium mt-2 truncate text-sm" title={g.title}>{g.title || "(untitled)"}</p>
                  )}
                  <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">
                    {(g.word_count || 0)} words · {new Date(g.created_at).toLocaleDateString()}
                  </p>
                  {(g.job_title || g.company) && (
                    <p className="text-xs text-[var(--brand-ink)]/60 mt-1">{[g.job_title, g.company].filter(Boolean).join(" · ")}</p>
                  )}
                  <div className="mt-4 flex gap-1 flex-wrap">
                    <Link to={`/vault-coach?section=${isCV ? "cv" : "cover"}&sub=saved`}
                      data-testid={`gen-open-${g.id}`}
                      className="btn-hard bg-white rounded-md px-2 py-1 text-xs flex items-center gap-1">
                      <ArrowRight size={12} weight="bold"/> open
                    </Link>
                    <button onClick={() => { setEditingGen(g.id); setGenRenameTo(g.title || ""); }}
                      data-testid={`gen-rename-${g.id}`}
                      className="btn-hard bg-white rounded-md px-2 py-1 text-xs flex items-center gap-1">
                      <PencilSimple size={12}/> rename
                    </button>
                    {!isMaster && (
                      <button onClick={async () => {
                        try { await api.post(`/saved-letters/${g.id}/promote-to-master`); toast.success("promoted to master ⭐"); load(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "promote failed"); }
                      }} data-testid={`gen-promote-${g.id}`}
                        className="btn-hard bg-[var(--pastel-mint)] rounded-md px-2 py-1 text-xs flex items-center gap-1"
                        title="make this the master">
                        <Star size={12}/> promote
                      </button>
                    )}
                    <button onClick={async () => {
                      if (!confirm("Delete this generated document?")) return;
                      try { await api.delete(`/saved-letters/${g.id}`); toast.success("deleted"); load(); }
                      catch { toast.error("delete failed"); }
                    }} data-testid={`gen-delete-${g.id}`}
                      className="btn-hard bg-white rounded-md px-2 py-1 text-xs flex items-center gap-1">
                      <Trash size={12}/> delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "uploaded" && <>

      <form onSubmit={upload} className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-8" data-testid="upload-form">
        <p className="label-tiny mb-4">/upload new</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="label-tiny block mb-1.5">file (PDF / PNG / JPG, &lt;10MB)</label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" required onChange={(e) => { setFile(e.target.files[0]); setRenameTo(""); }}
              data-testid="upload-file" className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            {file && (
              <div className="mt-2 flex items-start gap-2">
                <button type="button" onClick={suggestFilename} data-testid="suggest-filename"
                  className="btn-hard bg-[var(--pastel-yellow)] rounded-md px-2 py-1 text-xs flex items-center gap-1 shrink-0">
                  <MagicWand size={12} weight="bold"/> suggest employer-friendly name
                </button>
              </div>
            )}
            {renameTo && (
              <div className="mt-2">
                <label className="label-tiny block mb-1">will upload as</label>
                <input value={renameTo} onChange={(e) => setRenameTo(e.target.value)}
                  data-testid="upload-rename"
                  className="w-full card-hard rounded-md px-2 py-1 bg-white text-xs font-mono-d"/>
              </div>
            )}
          </div>
          <div>
            <label className="label-tiny block mb-1.5">type</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)}
              data-testid="upload-type" className="w-full card-hard rounded-md px-3 py-2 bg-white">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny block mb-1.5">expiry (optional)</label>
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              data-testid="upload-expiry" className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
          </div>
          <div className="md:col-span-3">
            <label className="label-tiny block mb-1.5">tags (comma sep)</label>
            <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. degree, 2024"
              data-testid="upload-tags" className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy || !file} data-testid="upload-submit"
              className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 w-full justify-center disabled:opacity-60 hover:bg-[var(--brand-ink)]">
              <UploadSimple size={16} weight="bold"/> {busy ? "uploading…" : "vault it"}
            </button>
          </div>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="docs-grid">
        {docs.length === 0 && <p className="text-sm text-[var(--brand-muted)]">no documents yet.</p>}
        {docs.map((d) => {
          const isPdf = d.content_type === "application/pdf";
          const Icon = isPdf ? FilePdf : FileImage;
          const days = expiryMap[d.id];
          return (
            <div key={d.id} className="card-hard rounded-md p-5 bg-white" data-testid={`doc-${d.id}`}>
              <div className="flex items-start justify-between">
                <Icon size={28} weight="duotone"/>
                <div className="flex items-center gap-1">
                  {d.is_primary && (
                    <span className="font-mono-d text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-mint)]" title="used by extension autofill">
                      primary
                    </span>
                  )}
                  <span className="label-tiny">{d.doc_type}</span>
                </div>
              </div>
              <p className="font-medium mt-3 truncate" title={d.original_filename}>{d.original_filename}</p>
              <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-1">
                {(d.size / 1024).toFixed(0)} KB {d.expiry && `· exp ${d.expiry}`}
              </p>
              <div className="mt-2"><ExpiryBadge days={days}/></div>
              {d.tags && <p className="text-xs text-[var(--brand-ink)]/60 mt-2">tags: {d.tags}</p>}
              <div className="mt-4 flex gap-2 flex-wrap">
                <button onClick={() => download(d.id, d.original_filename)} data-testid={`download-${d.id}`}
                  className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
                  <DownloadSimple size={14}/> open
                </button>
                {!d.is_primary && (
                  <button onClick={() => setPrimary(d.id)} data-testid={`primary-${d.id}`}
                    className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1"
                    title={`use as primary ${d.doc_type} for extension autofill`}>
                    <Star size={14}/> set primary
                  </button>
                )}
                <button onClick={() => remove(d.id)} data-testid={`delete-doc-${d.id}`}
                  className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
                  <Trash size={14}/> remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
      </>}
    </AppShell>
  );
}
